import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../Components/Header/Header';
import Sidebar from '../../Components/Sidebar/Sidebar';
import {
  getBookingsList,
  getProviderInbox,
  confirmBooking,
  declineBooking,
  cancelBooking,
  startBooking,
  completeBooking
} from '../../api/bookings';
import { getMyRegistrations, cancelEventRegistration, getProviderEventRegistrations } from '../../api/events';
import '../AdventureShared.css';
import ConfirmationModal from '../../Components/Shared/ConfirmationModal';
import './OrdersPage.css';

export default function OrdersPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize view mode based on pathname
  const initialMode = location.pathname.includes('/inbox') ? 'provider' : 'customer';
  const [viewMode, setViewMode] = useState(initialMode);

  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');

  // Phase 5 event ticket states
  const [eventRegistrations, setEventRegistrations] = useState([]);
  const [isEventsLoading, setIsEventsLoading] = useState(false);
  const [purchaseSubTab, setPurchaseSubTab] = useState('services');
  const [processingRegId, setProcessingRegId] = useState(null);

  // Track latest viewMode and subTab in refs to prevent async race conditions
  const latestViewMode = useRef(viewMode);
  const latestSubTab = useRef(purchaseSubTab);

  useEffect(() => {
    latestViewMode.current = viewMode;
  }, [viewMode]);

  useEffect(() => {
    latestSubTab.current = purchaseSubTab;
  }, [purchaseSubTab]);

  // Modals state
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  // Form states
  const [providerNotes, setProviderNotes] = useState('');
  const [declineReason, setDeclineReason] = useState('unavailable');
  const [declineMessage, setDeclineMessage] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [completionNotes, setCompletionNotes] = useState('');

  const [isStarting, setIsStarting] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [cancelRegConfirmId, setCancelRegConfirmId] = useState(null);
  const [bookingToStart, setBookingToStart] = useState(null);

  // Sync viewMode if location changes
  useEffect(() => {
    const pathMode = location.pathname.includes('/inbox') ? 'provider' : 'customer';
    if (pathMode !== viewMode) {
      setViewMode(pathMode);
    }
  }, [location.pathname]);

  const handleToggleViewMode = (mode) => {
    setViewMode(mode);
    if (mode === 'provider') {
      navigate('/facilitate/orders/inbox');
    } else {
      navigate('/facilitate/orders');
    }
  };

  const fetchBookings = useCallback(async () => {
    const expectedViewMode = viewMode;
    setIsLoading(true);
    setError('');
    try {
      let data = [];
      if (expectedViewMode === 'customer') {
        data = await getBookingsList({ role: 'customer' });
      } else {
        data = await getProviderInbox({ status: '' });
      }
      
      // Prevent race conditions: only update state if this matches current tab/view
      if (latestViewMode.current === expectedViewMode && latestSubTab.current === 'services') {
        setBookings(data);
      }
    } catch (err) {
      if (latestViewMode.current === expectedViewMode && latestSubTab.current === 'services') {
        console.error(`Failed to load orders for role ${expectedViewMode}:`, err);
        setError(expectedViewMode === 'customer' ? "Failed to load orders list." : "Failed to load incoming order requests.");
      }
    } finally {
      if (latestViewMode.current === expectedViewMode && latestSubTab.current === 'services') {
        setIsLoading(false);
      }
    }
  }, [viewMode]);

  const fetchEvents = useCallback(async () => {
    const expectedViewMode = viewMode;
    setIsEventsLoading(true);
    setError('');
    try {
      let data = [];
      if (expectedViewMode === 'customer') {
        data = await getMyRegistrations();
      } else {
        data = await getProviderEventRegistrations();
      }
      
      // Prevent race conditions: only update state if this matches current tab/view
      if (latestViewMode.current === expectedViewMode && latestSubTab.current === 'events') {
        setEventRegistrations(data);
      }
    } catch (err) {
      if (latestViewMode.current === expectedViewMode && latestSubTab.current === 'events') {
        console.error("Failed to load event registrations:", err);
        setError("Failed to load event tickets.");
      }
    } finally {
      if (latestViewMode.current === expectedViewMode && latestSubTab.current === 'events') {
        setIsEventsLoading(false);
      }
    }
  }, [viewMode]);

  useEffect(() => {
    if (purchaseSubTab === 'events') {
      fetchEvents();
    } else {
      fetchBookings();
    }
  }, [viewMode, purchaseSubTab, fetchBookings, fetchEvents]);

  // Reset active filter when viewMode or subTab toggles
  useEffect(() => {
    setActiveFilter('all');
  }, [viewMode, purchaseSubTab]);

  const formatPriceVal = (amount, model, currency) => {
    if (model === 'free') return 'Free';
    if (model === 'cod') return 'Cash on Delivery';

    let cleanCurrency = (currency || 'INR').trim().toUpperCase();
    if (cleanCurrency === 'RS') cleanCurrency = 'INR';
    if (!/^[A-Z]{3}$/.test(cleanCurrency)) cleanCurrency = 'INR';

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: cleanCurrency
      }).format(amount || 0);
    } catch (e) {
      return `${cleanCurrency} ${(amount || 0).toFixed(2)}`;
    }
  };

  const getFilteredBookings = () => {
    if (activeFilter === 'all') return bookings;
    return bookings.filter(b => b.status === activeFilter);
  };

  const getFilteredEvents = () => {
    if (activeFilter === 'all') return eventRegistrations;
    return eventRegistrations.filter(r => r.status === activeFilter);
  };

  const handleCancelEventReg = (regId) => {
    setCancelRegConfirmId(regId);
  };

  const handleConfirmCancelEventReg = async () => {
    if (!cancelRegConfirmId) return;
    setProcessingRegId(cancelRegConfirmId);
    try {
      await cancelEventRegistration(cancelRegConfirmId);
      alert("Tickets cancelled successfully.");
      setCancelRegConfirmId(null);
      fetchEvents();
    } catch (err) {
      console.error("Failed to cancel tickets", err);
      alert(err.message || "Failed to cancel tickets.");
    } finally {
      setProcessingRegId(null);
    }
  };

  // Actions
  const handleConfirmSubmit = async (e) => {
    e.preventDefault();
    setIsConfirming(true);
    try {
      await confirmBooking(selectedBooking.bookingId, providerNotes);
      setConfirmModalOpen(false);
      setSelectedBooking(null);
      setProviderNotes('');
      fetchBookings();
    } catch (err) {
      console.error(err);
      alert("Failed to confirm request.");
    } finally {
      setIsConfirming(false);
    }
  };

  const handleDeclineSubmit = async (e) => {
    e.preventDefault();
    setIsDeclining(true);
    try {
      await declineBooking(selectedBooking.bookingId, declineReason, declineMessage);
      setDeclineModalOpen(false);
      setSelectedBooking(null);
      setDeclineMessage('');
      fetchBookings();
    } catch (err) {
      console.error(err);
      alert("Failed to decline request.");
    } finally {
      setIsDeclining(false);
    }
  };

  const handleCancelSubmit = async (e) => {
    e.preventDefault();
    if (!cancelReason.trim()) return alert('Please enter cancellation reason.');
    setIsCancelling(true);
    try {
      await cancelBooking(selectedBooking.bookingId, cancelReason);
      setCancelModalOpen(false);
      setSelectedBooking(null);
      setCancelReason('');
      fetchBookings();
    } catch (err) {
      console.error(err);
      alert("Failed to cancel order.");
    } finally {
      setIsCancelling(false);
    }
  };

  const handleStartBookingAction = (booking) => {
    setBookingToStart(booking);
  };

  const handleConfirmStartBooking = async () => {
    if (!bookingToStart) return;
    setIsStarting(true);
    try {
      await startBooking(bookingToStart.bookingId);
      setBookingToStart(null);
      fetchBookings();
    } catch (err) {
      console.error(err);
      alert("Failed to mark service as started.");
    } finally {
      setIsStarting(false);
    }
  };

  const handleCompleteSubmit = async (e) => {
    e.preventDefault();
    setIsCompleting(true);
    try {
      await completeBooking(selectedBooking.bookingId, completionNotes);
      setCompleteModalOpen(false);
      setSelectedBooking(null);
      setCompletionNotes('');
      fetchBookings();
    } catch (err) {
      console.error(err);
      alert("Failed to mark service as completed.");
    } finally {
      setIsCompleting(false);
    }
  };

  const getFilters = () => {
    if (viewMode === 'provider') return ['all', 'pending', 'confirmed', 'in_progress', 'completed', 'cancelled'];
    if (purchaseSubTab === 'events') return ['all', 'registered', 'waitlisted', 'cancelled'];
    return ['all', 'pending', 'confirmed', 'in_progress', 'completed', 'cancelled'];
  };

  return (
    <div className="OrdersPage">
      <div className="adventurePageContainer">
        <div className="headerContain"><Header /></div>

        <div className="adventurePageContainer2">
          <div className="sidebarContain"><Sidebar /></div>

          <main className="adv-page-main">
            {/* View Switcher and Page Header */}
            <div className="adv-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
              <div>
                <h1 className="adv-page-title">
                  {viewMode === 'customer' ? (
                    <>My <span className="adv-page-title-accent">Orders</span></>
                  ) : (
                    <>Order Requests <span className="adv-page-title-accent">Inbox</span></>
                  )}
                </h1>
                <p className="adv-page-sub">
                  {viewMode === 'customer'
                    ? "Track and manage your service order requests."
                    : "Confirm, decline, or manage customer orders for your services."
                  }
                </p>
              </div>

              {/* View Mode Switcher Toggle */}
              <div className="adv-bookings-switcher">
                <button
                  className={`adv-bookings-switch-btn ${viewMode === 'customer' ? 'adv-bookings-switch-btn--active' : ''}`}
                  onClick={() => handleToggleViewMode('customer')}
                >
                  🧑 My Purchases
                </button>
                <button
                  className={`adv-bookings-switch-btn ${viewMode === 'provider' ? 'adv-bookings-switch-btn--active' : ''}`}
                  onClick={() => handleToggleViewMode('provider')}
                >
                  💼 My Sales
                </button>
              </div>
            </div>

            {/* Sub Tabs for Purchases / Sales */}
            <div className="adv-bookings-subtabs" style={{ display: 'flex', gap: '12px', margin: '0 0 20px 0', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '12px' }}>
              <button
                className={`adv-bookings-subtab-btn ${purchaseSubTab === 'services' ? 'adv-bookings-subtab-btn--active' : ''}`}
                onClick={() => { setPurchaseSubTab('services'); setActiveFilter('all'); }}
                style={{ background: purchaseSubTab === 'services' ? 'rgba(72,209,204,0.1)' : 'transparent', border: '1px solid', borderColor: purchaseSubTab === 'services' ? 'rgba(72,209,204,0.3)' : 'transparent', color: purchaseSubTab === 'services' ? '#48d1cc' : '#aaa', padding: '6px 14px', borderRadius: '20px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', transition: 'all 0.25s' }}
              >
                🛠️ Services & Products
              </button>
              <button
                className={`adv-bookings-subtab-btn ${purchaseSubTab === 'events' ? 'adv-bookings-subtab-btn--active' : ''}`}
                onClick={() => { setPurchaseSubTab('events'); setActiveFilter('all'); }}
                style={{ background: purchaseSubTab === 'events' ? 'rgba(155,81,224,0.1)' : 'transparent', border: '1px solid', borderColor: purchaseSubTab === 'events' ? 'rgba(155,81,224,0.3)' : 'transparent', color: purchaseSubTab === 'events' ? '#bca5ff' : '#aaa', padding: '6px 14px', borderRadius: '20px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', transition: 'all 0.25s' }}
              >
                🎟️ Event Tickets
              </button>
            </div>

            {/* Filter Tabs */}
            <div className="adv-filter-tabs">
              {getFilters().map(tab => (
                <button
                  key={tab}
                  className={`adv-filter-tab ${activeFilter === tab ? 'adv-filter-tab--active' : ''}`}
                  onClick={() => setActiveFilter(tab)}
                >
                  {tab === 'in_progress' ? 'In Progress' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            {/* Bookings & Requests List */}
            <div className="adv-list">
              {(isLoading || isEventsLoading) ? (
                <div className="adv-state-text">
                  Loading list...
                </div>
              ) : error ? (
                <div className="adv-state-error">{error}</div>
              ) : purchaseSubTab === 'events' ? (
                // Event Tickets / Registrations layout
                getFilteredEvents().length === 0 ? (
                  <div className="adv-empty">
                    <p>No event registrations found.</p>
                  </div>
                ) : (
                  getFilteredEvents().map((reg) => (
                    <div key={reg.registrationId} className="adv-booking-card" style={{ borderLeft: '4px solid #9b51e0' }}>
                      <div className="adv-booking-card__body" style={{ flex: 1, paddingLeft: '8px' }}>
                        <div className="adv-booking-card__top">
                          <h3 className="adv-booking-card__name">{reg.offeringTitle || "Event"}</h3>
                          <span className={`adv-status adv-status--${reg.status}`}>
                            {reg.status}
                          </span>
                        </div>
                        {viewMode === 'provider' && (
                          <p className="adv-booking-card__meta" style={{ fontSize: '13.5px', marginBottom: '8px' }}>
                            Customer: <strong style={{ color: '#48d1cc' }}>{reg.customerName || "User"}</strong>
                          </p>
                        )}
                        <p className="adv-booking-card__meta">Tickets: <strong>{reg.ticketCount}</strong></p>
                        {reg.attendeeNames && reg.attendeeNames.length > 0 && (
                          <p className="adv-booking-card__meta">Attendees: <strong>{reg.attendeeNames.join(", ")}</strong></p>
                        )}
                        {reg.specialRequirements && (
                          <p className="adv-booking-card__meta" style={{ fontStyle: 'italic', fontSize: '12.5px', color: '#aaa' }}>
                            Reqs: {reg.specialRequirements}
                          </p>
                        )}
                        {reg.shippingAddressLine && (
                          <p className="adv-booking-card__meta" style={{ color: '#ffb938', fontSize: '12.5px', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            {reg.offering?.category === 'service' ? '🏡 Service Address:' : '📦 Shipping Address:'} <strong>{reg.shippingAddressLine}, {reg.shippingCity}, {reg.shippingState} {reg.shippingZipcode}, {reg.shippingCountry}</strong>
                          </p>
                        )}
                        <p className="adv-booking-card__meta" style={{ color: '#888', fontSize: '11px', marginTop: '6px' }}>
                          Registered on {new Date(reg.createdAt).toLocaleDateString()}
                        </p>

                        {/* Fulfillments (Digital Downloads & Physical Orders) */}
                        {reg.fulfillments && reg.fulfillments.length > 0 && (
                          <div style={{ marginTop: '16px', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '12px' }}>
                            <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#48d1cc', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span>📦</span> Included Products / Tickets
                            </h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {reg.fulfillments.map((ful) => (
                                <div 
                                  key={ful.fulfillmentId || ful.id}
                                  style={{
                                    background: 'rgba(255,255,255,0.02)',
                                    border: '1px solid rgba(255,255,255,0.04)',
                                    borderRadius: '6px',
                                    padding: '10px',
                                    fontSize: '12px'
                                  }}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div>
                                      <strong style={{ color: '#fff' }}>{ful.product?.name}</strong>
                                      <span style={{ color: 'rgba(255,255,255,0.4)', marginLeft: '6px' }}>
                                        (Qty: {ful.quantity})
                                      </span>
                                    </div>
                                    <span className={`adv-status adv-status--${ful.status}`} style={{ fontSize: '10px', padding: '2px 6px' }}>
                                      {ful.status}
                                    </span>
                                  </div>
                                  
                                  {ful.product?.deliveryMethod === 'digital' && ful.product?.digitalFileUrl && ful.status === 'delivered' && (
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '6px' }}>
                                      <a
                                        href={ful.product.digitalFileUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="adv-btn-primary"
                                        style={{ fontSize: '11px', padding: '4px 10px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                      >
                                        📥 Download Digital Ticket/File
                                      </a>
                                    </div>
                                  )}

                                  {ful.product?.deliveryMethod === 'pre_ship' && (ful.status === 'dispatched' || ful.status === 'delivered') && (
                                    <div style={{ marginTop: '6px', padding: '6px', background: 'rgba(255,255,255,0.02)', borderRadius: '4px', fontSize: '11px', color: 'rgba(255,255,255,0.6)' }}>
                                      {ful.carrier && <span>Carrier: <strong>{ful.carrier}</strong></span>}
                                      {ful.trackingNumber && (
                                        <span style={{ marginLeft: '10px' }}>
                                          Tracking: <a
                                            href={`https://google.com/search?q=${encodeURIComponent(ful.carrier + ' tracking ' + ful.trackingNumber)}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: '#48d1cc', textDecoration: 'underline' }}
                                          >
                                            {ful.trackingNumber}
                                          </a>
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="adv-booking-card__actions" style={{ display: 'flex', flexDirection: 'column', gap: '8px', justifyContent: 'center' }}>
                        {(reg.status === 'registered' || reg.status === 'waitlisted') && (
                          <button
                            className="adv-btn-danger-outline"
                            disabled={processingRegId === reg.registrationId}
                            onClick={() => handleCancelEventReg(reg.registrationId)}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', minWidth: '130px' }}
                          >
                            {processingRegId === reg.registrationId ? (
                              <>
                                <div className="ev-dash-btn-spinner" style={{ width: '12px', height: '12px' }}></div>
                                Cancelling...
                              </>
                            ) : (
                              viewMode === 'customer'
                                ? (reg.status === 'waitlisted' ? 'Leave Waitlist' : 'Cancel Ticket')
                                : (reg.status === 'waitlisted' ? 'Remove from Waitlist' : 'Cancel Registration')
                            )}
                          </button>
                        )}
                        <button
                          className="adv-btn-primary"
                          onClick={() => navigate(`/facilitate/offerings/${reg.offeringId}`)}
                          style={{ minWidth: '130px' }}
                        >
                          View Offering
                        </button>
                      </div>
                    </div>
                  ))
                )
              ) : getFilteredBookings().length === 0 ? (
                <div className="adv-empty">
                  <p>No orders found for the selected filter.</p>
                </div>
              ) : (
                getFilteredBookings().map((booking) => (
                  viewMode === 'customer' ? (
                    // Customer Card Layout
                    <div key={booking.bookingId} className="adv-booking-card">
                      <img
                        src={booking.coverImageUrl || "https://images.unsplash.com/photo-1531403009284-440f080d1e12?auto=format&fit=crop&w=120&q=80"}
                        alt={booking.offeringTitle}
                        className="adv-booking-card__img"
                      />
                      <div className="adv-booking-card__body">
                        <div className="adv-booking-card__top">
                          <h3 className="adv-booking-card__name">{booking.offeringTitle}</h3>
                          <span className={`adv-status adv-status--${booking.status}`}>
                            {booking.status}
                          </span>
                        </div>
                        <p className="adv-booking-card__meta">Provider: <strong>{booking.providerName}</strong></p>
                        <p className="adv-booking-card__meta">
                          {booking.category === 'product' ? (
                            <>
                              🛒 Ordered on {new Date(booking.createdAt).toLocaleDateString()}
                              {booking.numberOfPeople ? ` (${booking.numberOfPeople} ${booking.numberOfPeople === 1 ? 'unit' : 'units'})` : ''}
                            </>
                          ) : (
                            <>
                              📅 {booking.date} at {booking.time}
                              {booking.duration ? ` (${booking.duration} ${booking.pricingModel === 'per_hour' ? 'hrs' : 'days'})` : ''}
                            </>
                          )}
                        </p>
                        <div className="adv-booking-card__price">
                          {booking.category === 'product'
                            ? `Total: ${new Intl.NumberFormat(undefined, { style: 'currency', currency: booking.currency || 'USD' }).format(booking.calculatedTotal)}`
                            : formatPriceVal(booking.price, booking.pricingModel, booking.currency)
                          }
                        </div>
                        {booking.shippingAddressLine && (
                          <p className="adv-booking-card__meta" style={{ marginTop: '8px', color: '#ffc107' }}>
                            🏡 {booking.category === 'product' ? 'Shipping Address' : 'Service Address'}: <strong>{booking.shippingAddressLine}, {booking.shippingCity}, {booking.shippingState} {booking.shippingZipcode}, {booking.shippingCountry}</strong>
                          </p>
                        )}
                      </div>

                      <div className="adv-booking-card__actions" style={{ display: 'flex', flexDirection: 'column', gap: '8px', justifyContent: 'center' }}>
                        <button className="adv-btn-primary" onClick={() => navigate(`/facilitate/orders/${booking.bookingId}`)}>
                          View Details
                        </button>
                        {booking.status === 'pending' && (
                          <button className="adv-btn-danger-outline" onClick={() => { setSelectedBooking(booking); setCancelReason(''); setCancelModalOpen(true); }}>
                            Cancel Request
                          </button>
                        )}
                        {booking.status === 'confirmed' && (
                          <button className="adv-btn-danger-outline" onClick={() => { setSelectedBooking(booking); setCancelReason(''); setCancelModalOpen(true); }}>
                            Cancel Order
                          </button>
                        )}
                        {booking.status === 'cancelled' && (
                          <button className="adv-btn-teal-outline" onClick={() => navigate(`/facilitate/offerings/${booking.offeringId}`)}>
                            Rebook Service
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    // Provider Card Layout
                    <div key={booking.bookingId} className="adv-request-card">
                      <div className="adv-request-card__top">
                        <div className="adv-request-card__customer">
                          <img
                            src={booking.customerAvatarUrl || "/profilePic.jpg"}
                            alt={booking.customerName}
                            className="adv-request-card__avatar"
                          />
                          <div>
                            <h4 className="adv-request-card__name">{booking.customerName}</h4>
                            <span className="adv-request-card__time">Requested {new Date(booking.createdAt).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <span className={`adv-status adv-status--${booking.status}`}>{booking.status}</span>
                      </div>

                      <div className="adv-request-card__detail-grid">
                        <div className="adv-request-card__left-col">
                          <strong
                            className="adv-detail-link"
                            onClick={() => navigate(`/facilitate/orders/${booking.bookingId}`)}
                          >
                            {booking.offeringTitle}
                          </strong>

                          {/* Metadata Inline Row */}
                          <div className="adv-request-card__meta-row">
                            {booking.category === 'product' ? (
                              <>
                                <div className="adv-meta-pill">
                                  <span>🛒</span> Ordered on {new Date(booking.createdAt).toLocaleDateString()}
                                </div>
                                <div className="adv-meta-pill">
                                  <span>📦</span> {booking.numberOfPeople} {booking.numberOfPeople === 1 ? 'unit' : 'units'}
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="adv-meta-pill">
                                  <span>📅</span> {booking.date} at {booking.time}
                                </div>
                                {booking.duration && (
                                  <div className="adv-meta-pill">
                                    <span>⏱️</span> {booking.duration} {booking.pricingModel === 'per_hour' ? 'hrs' : 'days'}
                                  </div>
                                )}
                              </>
                            )}
                          </div>

                          <p className="adv-detail-label" style={{ marginTop: '14px' }}>Customer Notes:</p>
                          <p className="adv-detail-notes">
                            {booking.notesToProvider || 'No notes left.'}
                          </p>
                          {booking.shippingAddressLine && (
                            <div style={{ marginTop: '14px' }}>
                              <p className="adv-detail-label">🏡 {booking.category === 'product' ? 'Shipping Address' : 'Service Address'}:</p>
                              <p className="adv-detail-notes" style={{ color: '#ffc107', fontWeight: '500' }}>
                                {booking.shippingAddressLine}, {booking.shippingCity}, {booking.shippingState} {booking.shippingZipcode}, {booking.shippingCountry}
                              </p>
                            </div>
                          )}
                        </div>

                        <div className="adv-request-card__right-col">
                          <div className="adv-request-card__price-section">
                            <span className="adv-price-title">Net Payout</span>
                            <span className="adv-price-val">
                              {formatPriceVal(booking.price, booking.pricingModel, booking.currency)}
                            </span>
                          </div>

                          <div className="adv-request-card__actions-row">
                            {booking.status === 'pending' && (
                              <>
                                <button className="adv-btn-danger-outline" onClick={() => { setSelectedBooking(booking); setDeclineModalOpen(true); }}>Decline</button>
                                <button className="adv-btn-primary" onClick={() => { setSelectedBooking(booking); setConfirmModalOpen(true); }}>Confirm Request</button>
                              </>
                            )}
                            {booking.status === 'confirmed' && (
                              <>
                                <button className="adv-btn-danger-outline" onClick={() => { setSelectedBooking(booking); setCancelModalOpen(true); }}>Cancel Order</button>
                                <button 
                                  className="adv-btn-primary" 
                                  onClick={() => handleStartBookingAction(booking)} 
                                  disabled={isStarting || booking.paymentStatus !== 'paid'}
                                  style={{ opacity: booking.paymentStatus !== 'paid' ? 0.6 : 1 }}
                                >
                                  {isStarting ? (
                                    booking.category === 'product' ? "Fulfilling..." : "Starting..."
                                  ) : booking.paymentStatus !== 'paid' ? (
                                    booking.category === 'product' ? "Fulfill Order (Awaiting Payment)" : "Start Service (Awaiting Payment)"
                                  ) : (
                                    booking.category === 'product' ? "Fulfill Order" : "Start Service"
                                  )}
                                </button>
                                {booking.paymentStatus !== 'paid' && (
                                  <p style={{ color: '#ffb938', fontSize: '11px', marginTop: '4px', textAlign: 'right', width: '100%', margin: '4px 0 0 0' }}>
                                    ⚠️ Awaiting customer payment
                                  </p>
                                )}
                              </>
                            )}
                            {booking.status === 'in_progress' && (
                              <button className="adv-btn-success" onClick={() => { setSelectedBooking(booking); setCompleteModalOpen(true); }}>
                                {booking.category === 'product' ? 'Complete Fulfillment' : 'Complete Service'}
                              </button>
                            )}
                            {booking.status === 'disputed' && (
                              <button
                                className="adv-btn-primary"
                                onClick={() => navigate(`/facilitate/orders/${booking.bookingId}`)}
                                style={{ background: '#ff7b00', borderColor: '#ff7b00' }}
                              >
                                Respond to Dispute
                              </button>
                            )}
                            {(booking.status === 'completed' || booking.status === 'cancelled' || booking.status === 'closed') && (
                              <button
                                className="adv-btn-teal-outline"
                                onClick={() => navigate(`/facilitate/orders/${booking.bookingId}`)}
                              >
                                View Details
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                ))
              )}
            </div>
          </main>
        </div>
      </div>

      {/* Confirm Request Modal (Provider) */}
      {confirmModalOpen && selectedBooking && (
        <div className="adv-modal-overlay">
          <div className="adv-modal">
            <h2 className="adv-modal__title">Confirm this order?</h2>
            <p className="adv-modal__desc">Confirming request from <strong>{selectedBooking.customerName}</strong> for <strong>{selectedBooking.offeringTitle}</strong> on {selectedBooking.date} at {selectedBooking.time}.</p>
            <form onSubmit={handleConfirmSubmit}>
              <div className="adv-modal__field">
                <label className="adv-modal__label">Notes to customer (optional)</label>
                <textarea rows="3" placeholder="Enter confirmation instructions, address specifics, or meeting links..." value={providerNotes} onChange={(e) => setProviderNotes(e.target.value)} className="adv-modal__textarea" />
              </div>
              <div className="adv-modal__actions">
                <button type="button" className="adv-btn-ghost" onClick={() => { setConfirmModalOpen(false); setSelectedBooking(null); setProviderNotes(''); }} disabled={isConfirming}>Cancel</button>
                <button
                  type="submit"
                  className="adv-btn-primary"
                  disabled={isConfirming}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
                >
                  {isConfirming ? (
                    <>
                      <span className="adv-btn-spinner"></span>
                      Confirming...
                    </>
                  ) : (
                    "Yes, Confirm Order"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Decline Booking Modal (Provider) */}
      {declineModalOpen && selectedBooking && (
        <div className="adv-modal-overlay">
          <div className="adv-modal">
            <h2 className="adv-modal__title">Decline this order?</h2>
            <p className="adv-modal__desc">Declining request from <strong>{selectedBooking.customerName}</strong>.</p>
            <form onSubmit={handleDeclineSubmit}>
              <div className="adv-modal__field">
                <label className="adv-modal__label">Reason *</label>
                <select value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} className="adv-modal__select">
                  <option value="unavailable">Unavailable</option>
                  <option value="not_a_fit">Not a fit</option>
                  <option value="scheduling">Scheduling conflict</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="adv-modal__field">
                <label className="adv-modal__label">Message to customer (optional)</label>
                <textarea rows="3" placeholder="Explain why you had to decline..." value={declineMessage} onChange={(e) => setDeclineMessage(e.target.value)} className="adv-modal__textarea" />
              </div>
              <div className="adv-modal__actions">
                <button type="button" className="adv-btn-ghost" onClick={() => { setDeclineModalOpen(false); setSelectedBooking(null); setDeclineMessage(''); }} disabled={isDeclining}>Go Back</button>
                <button
                  type="submit"
                  className="adv-btn-destructive"
                  disabled={isDeclining}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
                >
                  {isDeclining ? (
                    <>
                      <span className="adv-btn-spinner"></span>
                      Declining...
                    </>
                  ) : (
                    "Decline Order"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Cancel Booking Modal (Shared) */}
      {cancelModalOpen && selectedBooking && (
        <div className="adv-modal-overlay">
          <div className="adv-modal">
            <h2 className="adv-modal__title">Cancel this order?</h2>
            <form onSubmit={handleCancelSubmit}>
              <div className="adv-modal__field">
                <label className="adv-modal__label">Reason for cancellation *</label>
                <textarea required rows="4" placeholder={viewMode === 'customer' ? "Tell the provider why you are cancelling..." : "Explain to the customer why you must cancel..."} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} className="adv-modal__textarea" />
              </div>
              {viewMode === 'customer' && (
                <div className="adv-modal__warning">
                  <strong>Cancellation Policy:</strong> Cancellations within 24 hours of the scheduled time may not be eligible for a refund.
                </div>
              )}
              <div className="adv-modal__actions">
                <button type="button" className="adv-btn-ghost" onClick={() => { setCancelModalOpen(false); setSelectedBooking(null); setCancelReason(''); }} disabled={isCancelling}>Go Back</button>
                <button
                  type="submit"
                  className="adv-btn-destructive"
                  disabled={isCancelling}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
                >
                  {isCancelling ? (
                    <>
                      <span className="adv-btn-spinner"></span>
                      Cancelling...
                    </>
                  ) : (
                    "Yes, Cancel"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Complete Booking Modal (Provider) */}
      {completeModalOpen && selectedBooking && (
        <div className="adv-modal-overlay">
          <div className="adv-modal">
            <h2 className="adv-modal__title">
              {selectedBooking.category === 'product' ? 'Mark Order as Fulfilled' : 'Mark Service as Complete'}
            </h2>
            <p className="adv-modal__desc">
              {selectedBooking.category === 'product'
                ? 'Confirm delivery or shipment of the product to the client. Leave any completion remarks or tracking details below.'
                : 'Have you finished delivering this service? You can leave any summary notes below.'}
            </p>
            <form onSubmit={handleCompleteSubmit}>
              <div className="adv-modal__field">
                <label className="adv-modal__label">
                  {selectedBooking.category === 'product' ? 'Fulfillment Notes / Tracking details (optional)' : 'Completion Notes (optional)'}
                </label>
                <textarea rows="4" placeholder={selectedBooking.category === 'product' ? "Summarize shipping or delivery details..." : "E.g., All cleaning complete. Sanitized kitchen counters."} value={completionNotes} onChange={(e) => setCompletionNotes(e.target.value)} className="adv-modal__textarea" />
              </div>
              <div className="adv-modal__actions">
                <button type="button" className="adv-btn-ghost" onClick={() => { setCompleteModalOpen(false); setSelectedBooking(null); setCompletionNotes(''); }} disabled={isCompleting}>Cancel</button>
                <button
                  type="submit"
                  className="adv-btn-success"
                  disabled={isCompleting}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
                >
                  {isCompleting ? (
                    <>
                      <span className="adv-btn-spinner"></span>
                      {selectedBooking.category === 'product' ? 'Fulfilling...' : 'Completing...'}
                    </>
                  ) : (
                    selectedBooking.category === 'product' ? 'Mark Fulfilled' : 'Mark Complete'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={cancelRegConfirmId !== null}
        onClose={() => setCancelRegConfirmId(null)}
        onConfirm={handleConfirmCancelEventReg}
        title={viewMode === 'customer' ? "Cancel Event Registration?" : "Cancel Customer Registration?"}
        message={viewMode === 'customer' ? "Are you sure you want to cancel these tickets? This action cannot be undone." : "Are you sure you want to cancel this customer's registration? This action cannot be undone."}
        confirmText={viewMode === 'customer' ? "Yes, Cancel Tickets" : "Yes, Cancel Registration"}
        cancelText="Go Back"
        isDanger={true}
        isLoading={processingRegId !== null}
      />

      <ConfirmationModal
        isOpen={bookingToStart !== null}
        onClose={() => setBookingToStart(null)}
        onConfirm={handleConfirmStartBooking}
        title={bookingToStart?.category === 'product' ? "Fulfill Order?" : "Start Service Order?"}
        message={bookingToStart?.category === 'product' ? "Are you ready to begin fulfilling this order? This will notify the customer." : "Are you ready to begin? This will notify the customer."}
        confirmText={bookingToStart?.category === 'product' ? "Yes, Fulfill Order" : "Yes, Start Service"}
        cancelText="Cancel"
        isDanger={false}
        isLoading={isStarting}
      />
    </div>
  );
}
